import * as React from "react";
import {useEffect, useRef, useState} from "react";
import {translate} from "../../../translate/translate";
import {
    Box, Button, Fab,
    Grid,
    Paper, TextField,
    Typography
} from "@mui/material";
import TablePaginationComponent from "../../../components/table/table-pagination.component";
import {Search} from "@mui/icons-material";
import {PaginatedResult} from "../../../models/default/paginated-result";
import {ErrorComponent} from "../../../components/error/error.component";
import {ErrorMessage, Field, Form, Formik, FormikHelpers} from "formik";
import {useFilters} from "../../../filters/base-filter";
import {usePagination} from "../../../utils/pagination";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {enUS, ptBR} from "date-fns/locale";
import {ConvertDateToString} from "../../../components/date/date-timeFormat";
import {CostAllocation} from "../../../models/basic/cost-allocation";
import {PendingNreInvoiceFilter, PendingNreInvoiceService} from "../../../services/pending-nre-invoice-service";
import {orderByFn} from "../../../utils/order-by";


const PendingNreInvoiceReportComponent = () => {
    const service = new PendingNreInvoiceService();

    const {filters, updateFilterKeepingParam, updateFilter } = useFilters<PendingNreInvoiceFilter>(
        { initial_date: "", final_date: "", search: "", ordering: "" },
        ["initial_date", "final_date", "search", "ordering"]
    );
    const [list, setList] = React.useState<PaginatedResult<any>>();
    const [orderDirection, setOrderDirection] = React.useState<'asc' | 'desc' | ''>('asc');

    const locale = translate.language == "pt" ? ptBR : enUS;
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [invalidDate, setInvalidDate] = useState<boolean>(false);
    const [loading, setLoading] = React.useState(false);

    const {
        dataSource,
        pagination,
        resetPage,
        handleChangePage,
        handleChangeRowsPerPage
    } = usePagination<CostAllocation>(list);

    const expand = ["*"];

    const [loadingMaterial, setLoadingMaterial] = React.useState(false);

    useEffect(() => {
        initializeDates();
        search();
    }, []);

    const initializeDates = () => {
        const startDate = filters.initial_date;
        const finalDay = filters.final_date;

        if (startDate) {
            const startDateObj = new Date(startDate + "T12:00:00");
            setStartDate(startDateObj);
        }

        if (finalDay) {
            const finalDateObj = new Date(finalDay + "T12:00:00");
            setEndDate(finalDateObj);
        }


    }

    const search = (restartIndex = false) => {
        setLoading(true);
        service.readPaginated(filters, expand, pagination).then(response => {
            setList(response.data);
        }).catch((error: any) => {
            ErrorComponent(error);
        }).finally(() => {
            setLoading(false);
        })

        restartIndex && resetPage();
    }

    useEffect(() => {
        search();
    }, [pagination, filters.ordering]);

    const handleDateChange = (key: "initial_date" | "final_date", date: any) => {
        if (date?.toString().includes("Invalid Date")) {
            setInvalidDate(true);
            return;
        }

        updateFilterKeepingParam(key, date ? ConvertDateToString(date) : "");
        key === "initial_date" ? setStartDate(date) : setEndDate(date);

        const compareDate = new Date(filters[key === "initial_date" ? "final_date" : "initial_date"] + "T12:00:00");
        setInvalidDate(date && compareDate && (key === "initial_date" ? date > compareDate : compareDate > date));
    };

    const handleStartDateChange = (date: any) => handleDateChange("initial_date", date);
    const handleEndDateChange = (date: any) => handleDateChange("final_date", date);

    return (
        <section>
            <div className="floating-title" style={{display: "flex", flexDirection: "row", alignItems: "center"}}>
                <Typography variant={"h5"}>
                    Relatório de Notas Fiscais Pendentes de Valor NRE
                </Typography>
            </div>

            <Grid size={12}>
                <Paper variant="outlined">
                    <Box p={2}>
                        <Formik
                            enableReinitialize={true}
                            validateOnMount={true}
                            initialValues={{name: ""}}
                            onSubmit={(values, {setSubmitting}) => {
                                setTimeout(() => {
                                    search(true);
                                    setSubmitting(false);
                                }, 500);
                            }}
                        >
                            {
                                () => (
                                    <Form autoComplete="off">
                                        <Grid alignItems="center" container spacing={2} direction={"row"}>
                                            <Grid size={2}>
                                                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
                                                    <DatePicker
                                                        label={translate.t("initial_date")}
                                                        value={startDate}
                                                        onChange={handleStartDateChange}
                                                        inputRef={(props: any) => (
                                                            <TextField
                                                                {...props}
                                                                required={false}
                                                            />
                                                        )}
                                                        slotProps={{
                                                            textField: {
                                                                fullWidth: true,
                                                                required: false,
                                                            }
                                                        }}
                                                    />
                                                </LocalizationProvider>
                                            </Grid>
                                            <Grid size={2}>
                                                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
                                                    <DatePicker
                                                        label={translate.t("final_date")}
                                                        value={endDate}
                                                        onChange={handleEndDateChange}
                                                        inputRef={(props: any) => (
                                                            <TextField
                                                                {...props}
                                                                required={false}
                                                            />
                                                        )}
                                                        slotProps={{
                                                            textField: {
                                                                fullWidth: true,
                                                                required: false,
                                                            }
                                                        }}
                                                        minDate={new Date(filters.initial_date + "T12:00:00") || null}
                                                    />
                                                </LocalizationProvider>
                                            </Grid>
                                            <Grid size={4}>
                                                <Field
                                                    as={TextField}
                                                    name="search"
                                                    label={""}
                                                    fullWidth
                                                    variant={"outlined"}
                                                    autoFocus={true}
                                                    placeholder="Pesquisar por código, nota fiscal ou part number"
                                                    value={filters.search}
                                                    onChange={(event: React.ChangeEvent<{
                                                        value: unknown
                                                    }>) => updateFilterKeepingParam("search", event.target.value)}
                                                />
                                            </Grid>
                                            <Grid size={1}>
                                                <Fab
                                                    size="small"
                                                    type="submit"
                                                    color="secondary">
                                                    <Search/>
                                                </Fab>
                                            </Grid>
                                            {/*<Grid item xs={3} display="flex" justifyContent="end">*/}
                                            {/*    <Button*/}
                                            {/*        disabled={loadingDownload}*/}
                                            {/*        color="secondary"*/}
                                            {/*        variant="contained"*/}
                                            {/*        sx={{marginLeft: 'auto'}}*/}
                                            {/*        startIcon={<Download />}*/}
                                            {/*        onClick={() => {*/}
                                            {/*            export_xlsx();*/}
                                            {/*        }}>{translate.t("export").toUpperCase()}*/}
                                            {/*        {loadingDownload && <CircularProgress size="20px" sx={{marginLeft: '10px'}} />}*/}
                                            {/*    </Button>*/}
                                            {/*</Grid>*/}
                                        </Grid>
                                    </Form>
                                )}
                        </Formik>
                    </Box>
                </Paper>
            </Grid>

            <Paper variant="outlined" sx={{marginTop: "16px"}}>
                <TablePaginationComponent
                    state={dataSource}
                    displayedColumnsShow={["Cliente", "Projeto", "NF", "Data", "Amtz. QTD", "NRE", "Total", "Part Number", "Descrição"]}
                    displayedColumns={["client__name","project", "nf", "invoice_date", "amtz_qtd", "nre", "total", "part_number_display", "description"]}
                    numberColumns={["nre", "total", "amtz_qtd"]}
                    currencyColumns={["nre", "total"]}
                    integerColumns={["amtz_qtd"]}
                    dateColumns={["invoice_date"]}
                    count={list?.count}
                    next={list?.next}
                    previous={list?.previous}
                    page={pagination.page}
                    onPageChange={handleChangePage}
                    rowsPerPage={pagination.limit}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 50, 100]}
                    loading={loading}
                    orderByColumns={["part_number.project", "invoice_date"]}
                    orderBy={filters.ordering}
                    orderByFn={(column: string, orderDirection: any) => {
                      orderByFn(column, orderDirection, filters.ordering || '', setOrderDirection, updateFilterKeepingParam);
                    }}
                    orderDirection={orderDirection}
                />
            </Paper>

        </section>
    );
};

export default PendingNreInvoiceReportComponent;
